import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ConfirmModal } from './ConfirmModal';
import { refundPaymentSchema, type RefundPaymentInput } from '../schemas';
import { useRefundPayment } from '../hooks';
import type { AdminPaymentRow } from '../types';

interface Props {
  open: boolean;
  payment: AdminPaymentRow;
  onClose: () => void;
}

export function RefundPaymentModal({ open, payment, onClose }: Props) {
  const mutation = useRefundPayment(payment.userId);
  const form = useForm<RefundPaymentInput>({
    resolver: zodResolver(refundPaymentSchema),
    defaultValues: { reason: '', amount: payment.amount, maxAmount: payment.amount },
  });

  const submit = form.handleSubmit(async (values) => {
    await mutation.mutateAsync({
      paymentId: payment.id,
      amount: values.amount,
      reason: values.reason,
    });
    onClose();
  });

  return (
    <ConfirmModal
      open={open}
      title="Refund payment"
      description={`Original amount: ${payment.currency} ${payment.amount}`}
      confirmLabel="Refund"
      destructive
      loading={mutation.isPending}
      onClose={onClose}
      onConfirm={() => void submit()}
    >
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="block text-[10px] uppercase tracking-widest font-bold text-on-surface-variant mb-1">
            Amount
          </label>
          <input
            type="number"
            {...form.register('amount', { valueAsNumber: true })}
            className="w-full px-3 py-2 rounded-lg bg-surface-container-lowest border border-border-subtle text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30"
          />
          {form.formState.errors.amount && (
            <p className="text-xs text-red-300 mt-1">{form.formState.errors.amount.message}</p>
          )}
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest font-bold text-on-surface-variant mb-1">
            Reason
          </label>
          <textarea
            {...form.register('reason')}
            rows={3}
            className="w-full px-3 py-2 rounded-lg bg-surface-container-lowest border border-border-subtle text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30"
            placeholder="Why is this payment being refunded? (min 10 chars)"
          />
          {form.formState.errors.reason && (
            <p className="text-xs text-red-300 mt-1">{form.formState.errors.reason.message}</p>
          )}
        </div>
        {mutation.error && (
          <p className="text-xs text-red-300">{(mutation.error as Error).message}</p>
        )}
      </form>
    </ConfirmModal>
  );
}
